import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $createParagraphNode, $getNearestNodeFromDOMNode } from 'lexical';
import {
  $isLayoutContainerNode, $isLayoutItemNode,
  $createLayoutContainerNode, $createLayoutItemNode,
} from '../nodes/LayoutNode';

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseFr(template) {
  return template.trim().split(/\s+/).map(s => parseFloat(s) || 1);
}

function buildTemplate(frValues) {
  return frValues.map(v => `${Math.round(v * 1000) / 1000}fr`).join(' ');
}

function insertFrAt(template, index) {
  const parts = parseFr(template);
  parts.splice(index, 0, 1);
  return buildTemplate(parts);
}

function removeFrAt(template, index) {
  const parts = parseFr(template);
  parts.splice(index, 1);
  return buildTemplate(parts);
}

// Deep-clone a Lexical node subtree (uses the editor's registered node classes)
function deepCloneNode(editor, node) {
  const type      = node.getType();
  const entry     = editor._nodes.get(type);
  const NodeClass = entry?.klass ?? entry?.replace;   // support for replaced nodes
  if (!NodeClass) return null;

  let cloned;
  try {
    const json = node.exportJSON();
    cloned = NodeClass.importJSON(json);
  } catch (err) {
    return null;
  }

  if (typeof node.getChildren === 'function') {
    for (const child of node.getChildren()) {
      const childClone = deepCloneNode(editor, child);
      if (childClone) cloned.append(childClone);
    }
  }
  return cloned;
}

// Find [data-lexical-layout-item] ancestor + its index among siblings
function findLayoutItem(target, root) {
  let el = target;
  while (el && el !== root) {
    if (el.dataset && 'lexicalLayoutItem' in el.dataset) {
      const parent = el.parentElement;
      const siblings = parent
        ? Array.from(parent.querySelectorAll(':scope > [data-lexical-layout-item]'))
        : [];
      return { itemEl: el, colIndex: siblings.indexOf(el), containerEl: parent };
    }
    el = el.parentElement;
  }
  return null;
}

// ── Menu styles ───────────────────────────────────────────────────────────────

const menuStyle = {
  position: 'fixed',
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: 6,
  boxShadow: '0 4px 16px rgba(0,0,0,.15)',
  padding: '4px 0',
  minWidth: 190,
  zIndex: 99999,
  fontSize: 13,
  fontFamily: 'inherit',
};

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 14px',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const itemHoverStyle = {
  background: '#f5f0e8',
};

const dividerStyle = {
  height: 1,
  background: '#eee',
  margin: '4px 0',
};

const labelStyle = {
  display: 'block',
  padding: '4px 14px 2px',
  fontSize: 11,
  color: '#999',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
};

// ── MenuItem ──────────────────────────────────────────────────────────────────

function MenuItem({ icon, label, danger, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        ...itemStyle,
        ...(hovered && !disabled ? itemHoverStyle : {}),
        color: danger ? '#e03131' : disabled ? '#bbb' : 'inherit',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
    >
      <span style={{ width: 16, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      {label}
    </div>
  );
}

// ── Plugin ────────────────────────────────────────────────────────────────────

export default function LayoutContextMenuPlugin() {
  const [editor] = useLexicalComposerContext();
  const [menu, setMenu] = useState(null);
  const menuRef  = useRef(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  // ── Open menu on right-click inside a layout item ──────────────────────────

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    const onContextMenu = (e) => {
      const found = findLayoutItem(e.target, root);
      if (!found) return;

      e.preventDefault();
      const { colIndex, containerEl } = found;

      let containerKey   = null;
      let templateColumns = '';
      let colCount       = 0;
      let itemKey        = null;

      editor.read(() => {
        // Container node (the grid wrapper)
        let el = containerEl;
        while (el && el !== root) {
          const node = $getNearestNodeFromDOMNode(el);
          if ($isLayoutContainerNode(node)) {
            containerKey    = node.getKey();
            templateColumns = node.getTemplateColumns();
            const cols      = node.getChildren().filter($isLayoutItemNode);
            colCount        = cols.length;
            if (colIndex < cols.length) itemKey = cols[colIndex].getKey();
            break;
          }
          el = el.parentElement;
        }
      });

      if (!containerKey) return;

      setMenu({
        x: e.clientX,
        y: e.clientY,
        containerKey,
        templateColumns,
        colCount,
        colIndex,
        itemKey,
      });
    };

    root.addEventListener('contextmenu', onContextMenu);
    return () => root.removeEventListener('contextmenu', onContextMenu);
  }, [editor]);

  // ── Close on outside click / Escape ───────────────────────────────────────

  useEffect(() => {
    if (!menu) return;
    const onDown  = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu(); };
    const onKey   = (e) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown',   onKey);
    };
  }, [menu, closeMenu]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const addColumn = useCallback((afterIndex) => {
    if (!menu) return;
    closeMenu();
    editor.update(() => {
      const container = $getNodeByKey(menu.containerKey);
      if (!$isLayoutContainerNode(container)) return;

      const cols     = container.getChildren().filter($isLayoutItemNode);
      const newItem  = $createLayoutItemNode();
      newItem.append($createParagraphNode());

      if (afterIndex >= cols.length - 1) {
        container.append(newItem);
      } else {
        cols[afterIndex + 1].insertBefore(newItem);
      }

      container.setTemplateColumns(insertFrAt(menu.templateColumns, afterIndex + 1));
    });
  }, [editor, menu, closeMenu]);

  const duplicateColumn = useCallback(() => {
    if (!menu) return;
    closeMenu();
    editor.update(() => {
      const container = $getNodeByKey(menu.containerKey);
      if (!$isLayoutContainerNode(container)) return;

      const cols   = container.getChildren().filter($isLayoutItemNode);
      const source = cols[menu.colIndex];
      if (!source) return;

      const cloned = deepCloneNode(editor, source);
      if (!cloned) return;

      if (menu.colIndex >= cols.length - 1) {
        container.append(cloned);
      } else {
        cols[menu.colIndex + 1].insertBefore(cloned);
      }

      container.setTemplateColumns(insertFrAt(menu.templateColumns, menu.colIndex + 1));
    });
  }, [editor, menu, closeMenu]);

  const deleteColumn = useCallback(() => {
    if (!menu || menu.colCount <= 1) return;
    closeMenu();
    editor.update(() => {
      const container = $getNodeByKey(menu.containerKey);
      if (!$isLayoutContainerNode(container)) return;

      const cols   = container.getChildren().filter($isLayoutItemNode);
      const target = cols[menu.colIndex];
      if (!target) return;

      // Move content to the nearest surviving column
      const targetIdx = menu.colIndex > 0 ? menu.colIndex - 1 : 1;
      const sibling   = cols[targetIdx];
      target.getChildren().forEach(child => sibling.append(child));
      target.remove();

      container.setTemplateColumns(removeFrAt(menu.templateColumns, menu.colIndex));
    });
  }, [editor, menu, closeMenu]);

  const setColumnCount = useCallback((newCount) => {
    if (!menu || newCount < 1 || newCount > 12) return;
    editor.update(() => {
      const container = $getNodeByKey(menu.containerKey);
      if (!$isLayoutContainerNode(container)) return;

      const cols         = container.getChildren().filter($isLayoutItemNode);
      const currentCount = cols.length;

      if (newCount > currentCount) {
        for (let i = currentCount; i < newCount; i++) {
          const item = $createLayoutItemNode();
          item.append($createParagraphNode());
          container.append(item);
        }
      } else if (newCount < currentCount) {
        for (let i = currentCount - 1; i >= newCount; i--) {
          const extra  = cols[i];
          const keeper = cols[newCount - 1];
          extra.getChildren().forEach(c => keeper.append(c));
          extra.remove();
        }
      }

      // Rebuild template with equal columns
      const equal = Array(newCount).fill('1fr').join(' ');
      container.setTemplateColumns(equal);
    });
    setMenu(prev => prev ? { ...prev, colCount: newCount, templateColumns: Array(newCount).fill('1fr').join(' ') } : null);
  }, [editor, menu]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!menu) return null;

  // Keep menu within viewport
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(menu.x, vw - 200);
  const top  = Math.min(menu.y, vh - 320);

  return createPortal(
    <div
      ref={menuRef}
      style={{ ...menuStyle, left, top }}
      onContextMenu={e => e.preventDefault()}
    >
      {/* Column count control */}
      <span style={labelStyle}>Columns</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 14px 8px' }}>
        <button
          style={countBtn}
          disabled={menu.colCount <= 1}
          onMouseDown={(e) => { e.preventDefault(); setColumnCount(menu.colCount - 1); }}
        >−</button>
        <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
          {menu.colCount}
        </span>
        <button
          style={countBtn}
          disabled={menu.colCount >= 12}
          onMouseDown={(e) => { e.preventDefault(); setColumnCount(menu.colCount + 1); }}
        >+</button>
        <span style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>columns total</span>
      </div>

      <div style={dividerStyle} />

      {/* Column operations */}
      <span style={labelStyle}>This column</span>
      <MenuItem
        icon="⬅"
        label="Add column before"
        onClick={() => addColumn(menu.colIndex - 1)}
      />
      <MenuItem
        icon="➡"
        label="Add column after"
        onClick={() => addColumn(menu.colIndex)}
      />
      <MenuItem
        icon="⧉"
        label="Duplicate column"
        onClick={duplicateColumn}
      />

      <div style={dividerStyle} />

      <MenuItem
        icon="✕"
        label="Delete column"
        danger
        disabled={menu.colCount <= 1}
        onClick={deleteColumn}
      />
    </div>,
    document.body,
  );
}

// ── Shared button style ───────────────────────────────────────────────────────

const countBtn = {
  width: 24,
  height: 24,
  border: '1px solid #ddd',
  borderRadius: 4,
  background: '#f9f9f9',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  fontFamily: 'inherit',
};
